import { useEffect } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import GoTop from "./GoTop";
import { Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

function Layout() {
  const { i18n } = useTranslation();
  const { pathname, hash } = useLocation();

  // Scroll to top on route change, or to the anchored section when a hash is present
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
      return;
    }

    const timers: ReturnType<typeof setTimeout>[] = [];

    // Scroll instantly: a smooth scroll started while the page is still mounting
    // gets cancelled by the settling layout and leaves the user at the top.
    // Re-align a few times afterwards so late shifts (fonts, images, reveal
    // animations) don't drift the section back out of view — if it drifts off
    // screen its `once` in-view animation never fires and it stays invisible.
    const align = (el: Element) =>
      el.scrollIntoView({ behavior: "instant" as ScrollBehavior, block: "start" });

    // Lazy-loaded routes mount their chunk after this effect runs, so poll for
    // the target until it exists (or we give up) rather than scrolling once.
    let attempts = 0;
    const tryScroll = () => {
      const el = document.querySelector(hash);
      if (el) {
        align(el);
        [80, 200, 400, 700].forEach((delay) =>
          timers.push(setTimeout(() => align(el), delay))
        );
      } else if (attempts++ < 60) {
        timers.push(setTimeout(tryScroll, 50));
      }
    };
    tryScroll();
    return () => timers.forEach(clearTimeout);
  }, [pathname, hash]);

  return (
    <div
      dir={i18n.dir()}
      lang={i18n.language}
      className="min-h-screen bg-canvas text-brand"
    >
      <Navbar />
      <main className="pt-20">
        <Outlet />
      </main>
      <Footer />
      <GoTop />
    </div>
  );
}

export default Layout;
